import React, { useState, useEffect } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, DollarSign } from 'lucide-react';

const API = '/allowances';

const TYPE_OPTIONS = [
  'Expense',
  'Mileage/Travel',
  'Sleepover',
  'Travel',
  'Other',
];

const TYPE_ICONS = {
  'Sleepover': '🌙',
  'Mileage/Travel': '🚗',
  'Expense': '💰',
  'Travel': '✈️',
};

// ── Allowance modal ───────────────────────────────────────────────────────────
function AllowanceModal({ allowance, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                 allowance?.name                  || '',
    type:                 allowance?.type                  || 'Expense',
    category:             allowance?.category              || '',
    value:                allowance?.value != null ? String(allowance.value) : '',
    code:                 allowance?.code                  || '',
    externalId:           allowance?.external_id           || '',
    schadsClassification: allowance?.schads_classification || '',
    isTaxable:            allowance?.is_taxable !== false,
    paymentType:          allowance?.payment_type          || 'allowance',
    unit:                 allowance?.unit                  || 'flat',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSave() {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (allowance?.id) {
        const { data } = await api.put(`${API}/${allowance.id}`, form);
        onSaved(data, false);
        toast.success('Allowance updated');
      } else {
        const { data } = await api.post(API, form);
        onSaved(data, true);
        toast.success('Allowance created');
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{allowance?.id ? 'Edit Allowance' : 'New Allowance'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
              <input value={form.name} onChange={set('name')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Mileage"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
              <select value={form.type} onChange={set('type')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
              <input value={form.category} onChange={set('category')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="N/A"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Value ($)</label>
              <input type="number" step="0.01" min="0" value={form.value} onChange={set('value')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="N/A"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Code</label>
              <input value={form.code} onChange={set('code')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Mileage"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">External ID</label>
              <input value={form.externalId} onChange={set('externalId')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. 100"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Payment Type</label>
              <select value={form.paymentType} onChange={set('paymentType')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="allowance">Allowance</option>
                <option value="reimbursement">Reimbursement</option>
                <option value="loading">Loading</option>
                <option value="penalty">Penalty Rate</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Unit</label>
              <select value={form.unit} onChange={set('unit')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="flat">Flat Amount</option>
                <option value="per_km">Per KM</option>
                <option value="per_hour">Per Hour</option>
                <option value="per_shift">Per Shift</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">SCHADS Classification</label>
              <input value={form.schadsClassification} onChange={set('schadsClassification')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Clause 20.2"/>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <button type="button" onClick={() => setForm(f => ({ ...f, isTaxable: !f.isTaxable }))}
                className={`w-9 h-5 rounded-full transition-colors relative ${form.isTaxable ? 'bg-primary-500' : 'bg-slate-200'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isTaxable ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-xs font-medium text-slate-600">Taxable</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Allowances() {
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(API);
      setAllowances(data);
    } catch { toast.error('Failed to load allowances'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id) {
    try {
      await api.delete(`${API}/${id}`);
      toast.success('Allowance deleted');
      setAllowances(a => a.filter(x => x.id !== id));
      setDeleteConfirm(null);
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  }

  function onSaved(data, isNew) {
    if (isNew) setAllowances(a => [...a, data]);
    else setAllowances(a => a.map(x => x.id === data.id ? data : x));
  }

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Allowances"
        description="Payroll allowance types used in shifts and timesheets."
        actions={<button onClick={() => setModal({})} className="btn-primary"><Plus size={15} /> New Allowance</button>}
      />

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>
        ) : allowances.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign size={32} className="mx-auto text-slate-300 mb-2"/>
            <p className="text-slate-500 text-sm">No allowances yet</p>
            <button onClick={() => setModal({})} className="mt-3 text-primary-600 text-sm hover:underline">Add your first allowance</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Category</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Value</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Code</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">External ID</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {allowances.map(a => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50 group">
                  <td className="px-5 py-3 font-medium text-slate-700">{a.name}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {TYPE_ICONS[a.type] && <span className="mr-1">{TYPE_ICONS[a.type]}</span>}
                    {a.type || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{a.category || 'N/A'}</td>
                  <td className="px-4 py-3 text-slate-500">{a.value != null ? `$${parseFloat(a.value).toFixed(2)}` : 'N/A'}</td>
                  <td className="px-4 py-3 text-slate-600">{a.code || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{a.external_id || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100">
                      <button onClick={() => setModal(a)}
                        className="text-primary-600 text-xs font-medium hover:underline">EDIT</button>
                      <button onClick={() => setDeleteConfirm(a)}
                        className="text-red-500 text-xs font-medium hover:underline">DELETE</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <AllowanceModal allowance={modal?.id ? modal : null} onClose={() => setModal(null)} onSaved={onSaved}/>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-slate-800 mb-2">Delete Allowance?</h3>
            <p className="text-sm text-slate-500 mb-5">"{deleteConfirm.name}" will be removed from the system.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
